import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { LANDING_URL } from '../components/BrandLogo';
import { BadgeCheck, MapPin, Search, ArrowRight } from 'lucide-react';
import api from '../lib/api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';

const TIER = { 'Digitally Active': 'text-forest', 'Partially Visible': 'text-amber', 'Digitally Invisible': 'text-danger' };
const LA = { stockton: 'Stockton-on-Tees', middlesbrough: 'Middlesbrough', redcar: 'Redcar & Cleveland', hartlepool: 'Hartlepool', darlington: 'Darlington' };
const laName = (s) => LA[s] || s;

export default function VerifiedDirectory() {
  const [params, setParams] = useSearchParams();
  const district = params.get('district') || '';
  const sector = params.get('sector') || '';
  const page = Number(params.get('page') || 1);
  const [q, setQ] = useState(params.get('q') || '');
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get('/public/verified', { params: { district, sector, q: params.get('q') || '', page, limit: 24 } })
      .then(r => setData(r.data)).catch(() => setData({ total: 0, items: [], districts: [], sectors: [], pages: 1 }));
  }, [district, sector, page, params]);

  const set = (k, v) => { const p = new URLSearchParams(params); v ? p.set(k, v) : p.delete(k); if (k !== 'page') p.delete('page'); setParams(p); };

  return (
    <div className="min-h-screen bg-canvas" data-testid="verified-directory">
      <header className="bg-ink text-white">
        <div className="max-w-[1200px] mx-auto px-4 lg:px-6 py-10 sm:py-14">
          <a href={LANDING_URL} className="font-display text-lg font-extrabold tracking-[0.12em]" data-testid="directory-brand">ADOPTIC</a>
          <div className="flex items-center gap-2 mt-6 text-forest-light"><BadgeCheck className="w-5 h-5 text-brand-orange" /><span className="font-body text-xs uppercase tracking-[0.14em] text-white/60">Owner-verified register</span></div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold mt-2 leading-[1.05]">Verified Tees Valley businesses</h1>
          <p className="font-body text-base text-white/70 mt-4 max-w-2xl">Every profile below has been claimed and verified by its owner. Verified businesses rank first in every Adoptic search — by sector, category, name or postcode.</p>
          <form className="mt-6 flex gap-2 max-w-xl" onSubmit={e => { e.preventDefault(); set('q', q); }}>
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search verified businesses…" className="bg-white text-ink font-body" data-testid="verified-search-input" />
            <Button type="submit" className="bg-brand-orange hover:bg-brand-orange/90 text-white font-body" data-testid="verified-search-btn"><Search className="w-4 h-4" /></Button>
          </form>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-4 lg:px-6 py-8 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-8">
        <aside className="space-y-6">
          <Facet title="District" items={(data?.districts || []).map(d => ({ ...d, label: laName(d.name) }))} active={district} onPick={v => set('district', v)} testId="facet-district" />
          <Facet title="Sector" items={data?.sectors || []} active={sector} onPick={v => set('sector', v)} testId="facet-sector" />
          <div className="rounded-[10px] border border-ink/10 bg-white p-4">
            <p className="font-display text-sm font-extrabold text-ink">Own a Tees Valley business?</p>
            <p className="font-body text-xs text-ink/60 mt-1 mb-3">Claim it, verify ownership and appear here — ranked first.</p>
            <Button asChild size="sm" className="w-full bg-ink text-white font-body text-xs"><Link to="/login?next=%2Fsearch%3Fintent%3Dclaim" data-testid="verified-claim-cta">Claim my business <ArrowRight className="w-3.5 h-3.5 ml-1" /></Link></Button>
          </div>
        </aside>

        <section>
          <p className="font-body text-xs text-ink/50 mb-4" data-testid="verified-count">{data ? `${data.total.toLocaleString()} verified ${data.total === 1 ? 'business' : 'businesses'}` : 'Loading…'}{district && ` · ${laName(district)}`}{sector && ` · ${sector}`}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {(data?.items || []).map(b => (
              <Link key={b.id} to={`/b/${b.id}`} className="group rounded-[10px] border border-ink/10 bg-white p-5 hover:border-brand-orange/50 hover:shadow-md transition-[border-color,box-shadow]" data-testid={`verified-card-${b.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-display text-base font-extrabold text-ink leading-tight group-hover:text-brand-orange transition-colors">{b.company_name}</h3>
                  <BadgeCheck className="w-5 h-5 text-forest shrink-0" />
                </div>
                <p className="font-body text-xs text-ink/60 mt-2 flex items-center gap-1"><MapPin className="w-3 h-3" />{b.postcode} · {laName(b.primary_local_authority)}</p>
                <p className="font-body text-xs text-ink/50 mt-1 truncate">{b.sector}</p>
                <div className="flex items-center justify-between mt-4 pt-3 border-t border-ink/5">
                  <span className={`font-body text-[11px] font-semibold ${TIER[b.digital_tier] || 'text-ink/60'}`}>{b.digital_tier}</span>
                  <span className="font-mono text-xs text-ink/70">BDAF {Math.round(b.bdaf_score)}/5</span>
                </div>
              </Link>
            ))}
          </div>
          {data && data.total === 0 && <p className="font-body text-sm text-ink/50 py-10 text-center" data-testid="verified-empty">No verified businesses match yet.</p>}
          {data && data.pages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-8">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => set('page', String(page - 1))} className="font-body text-xs">Previous</Button>
              <span className="font-body text-xs text-ink/50">Page {page} of {data.pages}</span>
              <Button variant="outline" size="sm" disabled={page >= data.pages} onClick={() => set('page', String(page + 1))} className="font-body text-xs">Next</Button>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

const Facet = ({ title, items, active, onPick, testId }) => (
  <div data-testid={testId}>
    <p className="font-body text-[10px] uppercase tracking-[0.14em] text-ink/40 mb-2">{title}</p>
    <ul className="space-y-0.5">
      <li><button type="button" onClick={() => onPick('')} className={`w-full text-left px-2 py-1.5 rounded font-body text-xs ${!active ? 'bg-ink text-white' : 'text-ink/70 hover:bg-ink/5'}`}>All</button></li>
      {items.map(i => (
        <li key={i.name}><button type="button" onClick={() => onPick(i.name)} data-testid={`${testId}-${i.name}`} className={`w-full flex justify-between px-2 py-1.5 rounded font-body text-xs ${active === i.name ? 'bg-ink text-white' : 'text-ink/70 hover:bg-ink/5'}`}><span className="truncate">{i.label || i.name}</span><span className="opacity-60 ml-2">{i.count}</span></button></li>
      ))}
    </ul>
  </div>
);
